import type { FC, ReactNode } from 'react';

import Avatar from '@/common-components/Avatar';
import { cn } from '@/utils/cn.util';
import { formatRupees } from '@/utils/currency.util';
import type { AdminListingDetail, AdminListingListItem } from '@/types/rental.types';

import { CATEGORY_META } from '../constants';
import ListingStatusPill from './ListingStatusPill';

interface ListingDetailDrawerProps {
  selectedListing: AdminListingListItem | null;
  detail: AdminListingDetail | undefined;
  isLoadingDetail: boolean;
  isErrorDetail: boolean;
  mutatingId: string | null;
  onClose: () => void;
  onRemove: (listing: AdminListingListItem) => void;
  onRestore: (listing: AdminListingListItem) => void;
}

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

const InfoRow: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-[var(--color-divider)] py-2.5 last:border-b-0">
    <span className="shrink-0 text-[12.5px] font-medium text-[var(--color-ink-muted)]">
      {label}
    </span>
    <span className="text-right text-[13px] text-[var(--color-ink)]">{value}</span>
  </div>
);

const SectionHeading: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="mb-1 text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-ink-soft)]">
    {children}
  </p>
);

const ListingDetailDrawer: FC<ListingDetailDrawerProps> = ({
  selectedListing,
  detail,
  isLoadingDetail,
  isErrorDetail,
  mutatingId,
  onClose,
  onRemove,
  onRestore,
}) => {
  const isOpen = selectedListing !== null;
  const isMutating = !!(selectedListing && mutatingId === selectedListing.id);
  const meta = selectedListing ? CATEGORY_META[selectedListing.category] : null;

  const status = detail?.status ?? selectedListing?.status;
  const title = selectedListing?.title ?? '';
  const photo = detail?.photos[0] ?? selectedListing?.photos[0];

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-[480px] flex-col bg-white shadow-2xl',
          'overflow-hidden transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between border-b border-[var(--color-divider)] px-5 py-4">
          <h2 className="text-[15px] font-bold text-[var(--color-ink)]">Listing Details</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted-2)]"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
              <path d="M3.22 3.22a.75.75 0 0 1 1.06 0L8 6.94l3.72-3.72a.75.75 0 1 1 1.06 1.06L9.06 8l3.72 3.72a.75.75 0 1 1-1.06 1.06L8 9.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06L6.94 8 3.22 4.28a.75.75 0 0 1 0-1.06z" />
            </svg>
          </button>
        </div>

        {/* Hero */}
        <div
          className="flex h-[150px] shrink-0 items-center justify-center text-6xl"
          style={{ background: meta?.gradient ?? 'var(--color-surface-muted)' }}
        >
          {photo ? (
            <img src={photo} alt={title} className="h-full w-full object-cover" />
          ) : (
            <span>{meta?.icon}</span>
          )}
        </div>
        <div className="border-b border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-[17px] font-bold text-[var(--color-ink)]">{title}</div>
              <div className="mt-0.5 text-[12px] text-[var(--color-ink-soft)]">
                {meta?.label} · {detail?.campusName ?? selectedListing?.campusName ?? '—'}
              </div>
            </div>
            {status ? <ListingStatusPill status={status} /> : null}
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {isLoadingDetail ? (
            <div className="flex items-center justify-center py-16 text-[13px] text-[var(--color-ink-muted)]">
              Loading details…
            </div>
          ) : isErrorDetail ? (
            <div className="flex items-center justify-center py-16 text-[13px] text-[var(--color-danger-ink)]">
              Failed to load details. Try closing and reopening.
            </div>
          ) : detail ? (
            <>
              {/* Pricing */}
              <div className="px-5 py-4">
                <SectionHeading>Pricing</SectionHeading>
                <InfoRow
                  label="Price per day"
                  value={
                    <span className="font-bold text-brand-500">
                      {formatRupees(detail.pricePerDayPaise)}
                    </span>
                  }
                />
                <InfoRow
                  label="Security deposit"
                  value={detail.deposit.enabled ? formatRupees(detail.deposit.amountPaise) : 'None'}
                />
              </div>

              <div className="mx-5 border-t border-[var(--color-divider)]" />

              {/* Owner */}
              <div className="px-5 py-4">
                <SectionHeading>Owner</SectionHeading>
                <div className="flex items-center gap-3 py-2">
                  <Avatar name={detail.owner.name} src={detail.owner.avatarUrl} size="md" />
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold text-[var(--color-ink)]">
                      {detail.owner.name}
                    </div>
                    <div className="text-[11.5px] text-[var(--color-ink-muted)]">
                      {[detail.owner.year, detail.owner.stream].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </div>
                  <div className="ml-auto text-right text-[11.5px] text-[var(--color-ink-muted)]">
                    <div>Trust {detail.owner.trustScore}</div>
                    {detail.owner.ratingAvg > 0 ? (
                      <div>★ {detail.owner.ratingAvg.toFixed(1)}</div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="mx-5 border-t border-[var(--color-divider)]" />

              {/* Performance */}
              <div className="px-5 py-4">
                <SectionHeading>Performance</SectionHeading>
                <InfoRow label="Times Rented" value={detail.timesRented} />
                <InfoRow label="Active Rentals Now" value={detail.activeRentalCount} />
                <InfoRow
                  label="Rating"
                  value={
                    detail.ratingCount > 0
                      ? `★ ${detail.ratingAvg.toFixed(1)} (${detail.ratingCount} review${detail.ratingCount === 1 ? '' : 's'})`
                      : 'No ratings yet'
                  }
                />
                <InfoRow
                  label="Reports"
                  value={
                    detail.reportCount > 0 ? (
                      <span className="font-semibold text-[var(--color-danger-ink)]">
                        ⚠️ {detail.reportCount}
                      </span>
                    ) : (
                      'None'
                    )
                  }
                />
              </div>

              {detail.description ? (
                <>
                  <div className="mx-5 border-t border-[var(--color-divider)]" />
                  <div className="px-5 py-4">
                    <SectionHeading>Description</SectionHeading>
                    <p className="py-1 text-[13px] leading-relaxed text-[var(--color-ink-muted)]">
                      {detail.description}
                    </p>
                  </div>
                </>
              ) : null}

              {detail.recentReviews.length > 0 ? (
                <>
                  <div className="mx-5 border-t border-[var(--color-divider)]" />
                  <div className="px-5 py-4">
                    <SectionHeading>Recent Reviews</SectionHeading>
                    <div className="flex flex-col gap-2.5 pt-1">
                      {detail.recentReviews.map((review) => (
                        <div
                          key={review.id}
                          className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] p-3"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[12px] font-semibold text-[var(--color-ink)]">
                              {review.authorName}
                            </span>
                            <span className="text-[11px] text-[var(--color-warn-ink)]">
                              {'★'.repeat(Math.round(review.rating))}
                            </span>
                          </div>
                          {review.comment ? (
                            <p className="mt-1 text-[12px] leading-relaxed text-[var(--color-ink-muted)]">
                              “{review.comment}”
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}

              <div className="mx-5 border-t border-[var(--color-divider)]" />

              {/* Meta */}
              <div className="px-5 py-4">
                <SectionHeading>Listing</SectionHeading>
                <InfoRow label="Created" value={formatDate(detail.createdAt)} />
                <InfoRow label="Last Updated" value={formatDate(detail.updatedAt)} />
              </div>
            </>
          ) : null}
        </div>

        {/* Footer action */}
        {selectedListing && status ? (
          <div className="border-t border-[var(--color-divider)] bg-white px-5 py-4">
            {status === 'removed' ? (
              <button
                type="button"
                disabled={isMutating}
                onClick={() => onRestore(selectedListing)}
                className="w-full rounded-lg border border-[var(--color-success)] bg-[var(--color-success-bg)] py-2.5 text-[13px] font-semibold text-[var(--color-success-ink)] transition-colors hover:bg-[var(--color-success)] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isMutating ? 'Restoring…' : 'Restore Listing'}
              </button>
            ) : (
              <button
                type="button"
                disabled={isMutating}
                onClick={() => onRemove(selectedListing)}
                className="w-full rounded-lg border border-[var(--color-danger)] bg-[var(--color-danger-bg)] py-2.5 text-[13px] font-semibold text-[var(--color-danger-ink)] transition-colors hover:bg-[var(--color-danger)] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isMutating ? 'Removing…' : 'Remove Listing'}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default ListingDetailDrawer;
